import { AxiosError } from 'axios';

/**
 * Extracts a user-friendly error message from an Axios error
 * Prioritizes error messages from the API response over generic Axios errors
 */
export function getErrorMessage(error: unknown): string {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<{
      success?: boolean;
      error?: string;
      message?: string;
      errorCode?: string;
      validationErrors?: any;
    }>;

    // Helper to sanitize HTML strings (e.g. from 404/502/proxy error pages)
    const sanitizeMessage = (msg: unknown): string | null => {
      if (typeof msg !== 'string') return null;
      const trimmed = msg.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || /<[a-z][\s\S]*>/i.test(trimmed)) {
        return null;
      }
      return trimmed;
    };

    // First, try to get the error message from the response data
    if (axiosError.response?.data) {
      const data = axiosError.response.data;

      // Check for error field (our API format)
      const sanitizedError = sanitizeMessage(data.error);
      if (sanitizedError) {
        return sanitizedError;
      }

      // Check for message field (alternative format)
      const sanitizedMsg = sanitizeMessage(data.message);
      if (sanitizedMsg) {
        return sanitizedMsg;
      }

      // If data is a string, check if it's not HTML
      const sanitizedData = sanitizeMessage(data);
      if (sanitizedData) {
        return sanitizedData;
      }
    }

    // For network errors (no response)
    if (!axiosError.response) {
      if (axiosError.code === 'ERR_NETWORK' || axiosError.message?.includes('Network Error')) {
        return 'Network error. Please check your internet connection and try again.';
      }
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return 'Request timed out. Please try again.';
      }
      return 'Unable to connect to the server. Please try again later.';
    }

    // For HTTP errors without a specific message, provide user-friendly defaults
    const status = axiosError.response.status;
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication failed. Please check your credentials.';
      case 403:
        return 'Insufficient permissions for this operation. Please contact your administrator.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. Please refresh and try again.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Bad gateway. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      case 504:
        return 'Gateway timeout. Please try again later.';
      default:
        return `An error occurred (Status: ${status}). Please try again.`;
    }
  }

  // Helper to check if string contains raw HTML
  const isHtmlString = (str: string): boolean => {
    const trimmed = str.trim();
    return trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || /<[a-z][\s\S]*>/i.test(trimmed);
  };

  // Handle standard Error objects (from our API service)
  if (error instanceof Error) {
    if (isHtmlString(error.message)) {
      return 'An unexpected server error occurred. Please try again.';
    }
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    if (isHtmlString(error)) {
      return 'An unexpected server error occurred. Please try again.';
    }
    return error;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Extracts validation errors from an error response
 */
export function getValidationErrors(error: unknown): Record<string, string[]> | null {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<{ validationErrors?: any }>;
    if (axiosError.response?.data?.validationErrors) {
      return axiosError.response.data.validationErrors;
    }
  }

  // Check if error object has validationErrors property (from our API service)
  if (error && typeof error === 'object' && 'validationErrors' in error) {
    const errorObj = error as any;
    if (errorObj.validationErrors) {
      return errorObj.validationErrors;
    }
  }

  return null;
}

/**
 * Prefer the first field validation message over generic API error text (e.g. "Invalid input data").
 */
export function getPrimaryApiErrorMessage(error: unknown): string {
  const ve = getValidationErrors(error);
  if (ve) {
    for (const msgs of Object.values(ve)) {
      if (Array.isArray(msgs)) {
        const m = msgs.find((x) => typeof x === 'string' && x.trim());
        if (m) return m;
      }
    }
  }
  return getErrorMessage(error);
}

/**
 * Gets the error code from an error response
 */
export function getErrorCode(error: unknown): string | null {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<{ errorCode?: string }>;
    if (axiosError.response?.data?.errorCode) {
      return axiosError.response.data.errorCode;
    }
  }

  // Check if error object has errorCode property (from our API service)
  if (error && typeof error === 'object' && 'errorCode' in error) {
    const errorObj = error as any;
    if (errorObj.errorCode) {
      return errorObj.errorCode;
    }
  }

  return null;
}

/**
 * Checks if an error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return !axiosError.response || axiosError.code === 'ERR_NETWORK';
  }
  return false;
}

/**
 * Checks if an error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status ? axiosError.response.status >= 500 : false;
  }
  return false;
}

